import SubmissionNavigationDropdown from 'components/Grading/SubmissionNavigationDropdown';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/Grading/PageProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Skeleton, Stack, styled, Tag, Text } from 'shell/components';
import { getGradingNavigationTitle } from 'utils/gradingNavigation';

const GRADING_STATUS_TAG_VALUES = {
    not_started: 'readyForReview',
    in_progress: 'partiallyReviewed',
    confirmed: 'reviewed',
};

const Header = () => {
    const { t } = useTranslation();
    const { device } = useViewport();

    const {
        currentAssignment,
        submissions,
        headerNavigationOptions,
        sectionOptions,
        sectionId,
        isGroupAssignment,
        currentSubmission,
        gradingStatus,
        selectedFileId,
        sort,
        setSort,
        isGradesGenerating,
        handleClickNext,
        handleClickPrevious,
        handleSelectSubmission,
        handleSelectSection,
    } = useContext(PageContext);

    const isNavigationDisabled = isGradesGenerating || headerNavigationOptions.length <= 1;
    const gradingStatusTagValue = GRADING_STATUS_TAG_VALUES[gradingStatus];
    const currentFile = currentSubmission.files?.find(({ id }) => id === selectedFileId);
    const currentFileTitle = currentSubmission.api
        ? getGradingNavigationTitle(currentSubmission, currentAssignment)
        : currentFile?.original_file
          ? currentFile?.original_file.name
          : currentFile?.name;

    const SubmissionTitle =
        currentFile || currentSubmission.type === 'quiz' ? (
            <Text component={'h1'} noWrap title={currentFileTitle} variant={'p1'}>
                {currentFileTitle}
            </Text>
        ) : (
            <Skeleton sx={{ fontSize: '1rem' }} width={200} />
        );

    const getSubmissionTimestamp = (dateValue) => (
        <Text color={'light'} noWrap variant={'p3'}>
            {t('ui.uploaded', 'Uploaded')}: {t('ui.format-date-short', { val: dateValue })} (
            {t('ui.format-time-only', { val: dateValue })})
        </Text>
    );

    const SubmissionHeading = (
        <Stack sx={{ flex: 1, minWidth: 0 }} alignItems={'center'} direction={'row'} spacing={4}>
            <Stack sx={{ minWidth: 0, flexShrink: 1 }} spacing={1}>
                {SubmissionTitle}
                {getSubmissionTimestamp(currentSubmission.api?.file?.submitted_on ?? currentSubmission.created_on)}
            </Stack>
            {gradingStatusTagValue && (
                <Tag sx={{ flexShrink: 0 }} type={'gradingStatus'} value={gradingStatusTagValue} />
            )}
        </Stack>
    );

    const NavigationDropdown = (
        <SubmissionNavigationDropdown
            disabled={isGradesGenerating}
            currentSubmission={currentSubmission}
            options={headerNavigationOptions}
            sectionOptions={sectionOptions}
            sectionId={sectionId}
            totalSubmissions={submissions.length}
            isGroupAssignment={isGroupAssignment}
            sort={sort}
            setSort={setSort}
            selectSubmission={handleSelectSubmission}
            selectSection={handleSelectSection}
        />
    );

    const NavigationControls = (
        <Stack sx={{ flexShrink: 0 }} alignItems={'center'} direction={'row'} spacing={4}>
            <IconButton
                aria-label={t('controls.previous-submission', 'Previous submission')}
                disabled={isNavigationDisabled}
                kind={'chevronLeft'}
                size={'small'}
                tooltip={{ component: t('controls.previous-submission', 'Previous submission') }}
                variant={'square'}
                onClick={handleClickPrevious}
            />
            {NavigationDropdown}
            <IconButton
                aria-label={t('controls.next-submission', 'Next submission')}
                disabled={isNavigationDisabled}
                kind={'chevronRight'}
                size={'small'}
                tooltip={{ component: t('controls.next-submission', 'Next submission') }}
                variant={'square'}
                onClick={handleClickNext}
            />
        </Stack>
    );

    return device === 'mobile' ? (
        <RootStack device={device} justifyContent={'space-between'}>
            {SubmissionHeading}
            <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-end'}>
                {NavigationControls}
            </Stack>
        </RootStack>
    ) : (
        <RootStack alignItems={'center'} device={device} direction={'row'} justifyContent={'space-between'} spacing={4}>
            {SubmissionHeading}
            {NavigationControls}
        </RootStack>
    );
};

const RootStack = styled(Stack)(({ theme, device }) => {
    return {
        position: 'absolute',
        top: 0,
        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        width: '100%',
        height: device === 'mobile' ? '109px' : '65px',
        padding: device === 'mobile' ? theme.spacing(3, 4) : theme.spacing(3, 5),
        background: theme.palette.ensightful.primary[50],
    };
});

export default Header;
